import { useTranslation } from 'react-i18next';
import {
    useTheme,
    Typography,
    Grid,
    Divider,
    Stack,
    PieChart,
    Pie,
    Cell,
    ChartTooltip,
} from '../../../../base';
import {
    IClientTimeToHireComparisonData,
    IPieLabelProps,
    TIME_TO_HIRE_METRIC_KEYS,
    TIME_TO_HIRE_METRIC_LABELS,
    TIME_TO_HIRE_CHART_COLORS,
    RADIAN,
} from '../../../../common';
import {
    StyledPieCard,
    StyledPieCardHeader,
    StyledPieBody,
    StyledPieLegendRow,
    StyledColorDot,
} from './JobComparisonView.styles';
import JobMetricChartTooltip from '../JobSpecificView/JobMetricChartTooltip';

interface IComparisonPieChartsProps {
    data: IClientTimeToHireComparisonData;
}

const ComparisonPieCharts = ({ data }: IComparisonPieChartsProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const renderPercentLabel = ({
        cx,
        cy,
        midAngle,
        outerRadius,
        percent,
    }: IPieLabelProps) => {
        if (percent < 0.05) return null;
        const radius = outerRadius * 0.7;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);
        return (
            <text
                x={x}
                y={y}
                fill={theme.palette.common.white}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={11}
                fontWeight={500}
            >
                {`${Math.round(percent * 100)}%`}
            </text>
        );
    };

    return (
        <Grid container spacing={2}>
            {TIME_TO_HIRE_METRIC_KEYS.map((key) => {
                const pieData = data.jobs.map((job, index) => {
                    const days = Math.round(job[key]?.averageDays ?? 0);
                    return {
                        name: job.jobTitle,
                        value: Math.max(days, 0.1),
                        color: TIME_TO_HIRE_CHART_COLORS[
                            index % TIME_TO_HIRE_CHART_COLORS.length
                        ],
                        days,
                    };
                });

                return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                        <StyledPieCard>
                            <StyledPieCardHeader>
                                <Typography
                                    variant="body2"
                                    color={theme.palette.grey[600]}
                                >
                                    {t(
                                        'reportsPage.timeToHireReport.jobComparison.stepLabel',
                                        {
                                            metric: t(
                                                TIME_TO_HIRE_METRIC_LABELS[key]
                                            ),
                                        }
                                    )}
                                </Typography>
                            </StyledPieCardHeader>
                            <Divider
                                sx={{
                                    borderColor: theme.palette.grey[300],
                                }}
                            />
                            <StyledPieBody>
                                <PieChart width={180} height={180}>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        dataKey="value"
                                        stroke="none"
                                        label={renderPercentLabel}
                                        labelLine={false}
                                    >
                                        {pieData.map((entry) => (
                                            <Cell
                                                key={entry.name}
                                                fill={entry.color}
                                            />
                                        ))}
                                    </Pie>
                                    <ChartTooltip
                                        content={JobMetricChartTooltip as never}
                                    />
                                </PieChart>
                                <Stack spacing={1}>
                                    {pieData.map((entry) => (
                                        <StyledPieLegendRow
                                            container
                                            key={entry.name}
                                        >
                                            <StyledColorDot
                                                dotColor={entry.color}
                                            />
                                            <Typography variant="caption">
                                                {`${entry.name} - ${entry.days} ${t('reportsPage.timeToHireReport.days')}`}
                                            </Typography>
                                        </StyledPieLegendRow>
                                    ))}
                                </Stack>
                            </StyledPieBody>
                        </StyledPieCard>
                    </Grid>
                );
            })}
        </Grid>
    );
};

export default ComparisonPieCharts;
